import './style.scss'
import { Badge, Button, message, Popover, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect, useRef, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import * as HomeApi from '@/api/home'
import { IconWithdraw } from '@/assets/svg/icon-withdraw'
import { eventTracking, urlSource } from '@/libs/util'

const Distribution: FC = () => {
  const { t: translate } = useTranslation(['promoter', 'system'])
  const [profile, setProfile] = useState<any>()
  const [orders, setOrders] = useState<any[]>([])
  const [invites, setInvites] = useState<any[]>([])
  const logsRef = useRef<any>()

  useEffect(() => {
    eventTracking('MyDistrubutionPage')
    getProfile()
    getOrders()
    getInvites()
  }, [])

  const getProfile = async () => {
    const res = await HomeApi.getDistributionDetail()
    setProfile(res)
  }

  const getOrders = async () => {
    const res = await HomeApi.getDistributionOrders()
    setOrders(res?.list || [])
  }

  const getInvites = async () => {
    const res = await HomeApi.getDistributionInvitees()
    setInvites(res?.list || [])
  }

  return (
    <div className="page-common page-distribution">
      <div className="common-header">{translate('promoter.distribution_promotion')}</div>

      <div className="common-wrapper">
        <div className="page-distribution-wrapper">
          <div className="page-distribution-main">
            <div
              className="header-img"
              onClick={() => {
                eventTracking('OpenDistrubutionForm')
                window.open('https://lingverse.feishu.cn/share/base/form/shrcnh5QofNOhjLSyAPj5Nl0U7f')
              }}
            >
              <img src={urlSource(`hiflys/point-activity/images/promter-0422.webp?v=${Date.now()}`)} />
            </div>
            <div className="distribution-header">
              <div className="distribution-info">
                <div className="info-item">
                  <div className="text">{translate('promoter.expected_income')}</div>
                  <div className="num">{(profile?.expected_gain_amount || 0) / 100}</div>
                </div>
                <div className="info-item">
                  <div className="text">{translate('promoter.withdrawable_amount')}</div>
                  <div className="num">
                    <span>
                      {(profile?.withdrawable_amount || 0) / 100}
                      {(profile?.withdrawable_amount || 0) > 0 && (
                        <Popover
                          trigger={'click'}
                          content={
                            <div style={{ padding: 12 }}>
                              <img
                                style={{ width: 200, height: 200 }}
                                src={urlSource(`hiflys/point-activity/images/qrcode-company.png?v=${Date.now()}`)}
                              />
                            </div>
                          }
                          title={
                            <div style={{ padding: '0 12px', textAlign: 'center' }}>
                              {translate('promoter.scan_qr_code')}
                            </div>
                          }
                        >
                          <Button className="btn" icon={<IconWithdraw />} type="primary">
                            {translate('promoter.withdraw')}
                          </Button>
                        </Popover>
                      )}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <div className="text">{translate('promoter.withdrawn_amount')}</div>
                  <div className="num">{(profile?.withdrawn_amount || 0) / 100}</div>
                </div>
                <div className="info-item">
                  <div className="text">{translate('promoter.total_orders')}</div>
                  <div className="num">{profile?.order_count || 0}</div>
                </div>
                <div className="info-item">
                  <div className="text">{translate('promoter.total_promotions')}</div>
                  <div className="num">{profile?.invitee_count || 0}</div>
                </div>
              </div>
            </div>

            {profile?.code && (
              <div className="distribution-link">
                <div className="text">{translate('promoter.exclusive_link')}</div>
                <div className="link">
                  <div className="h">{`${location.origin}/p/${profile?.code}`}</div>
                </div>
                <CopyToClipboard
                  text={`${location.origin}/p/${profile?.code || ''}`}
                  onCopy={() => {
                    message.success(translate('promoter.link_copied'))
                  }}
                >
                  <Button type="primary">{translate('promoter.copy_link')}</Button>
                </CopyToClipboard>
              </div>
            )}

            <div className="distribution-table mt32" ref={logsRef}>
              <div className="table-title">{translate('promoter.order_details')}</div>
              <Table
                pagination={
                  orders?.length > 8
                    ? {
                        pageSize: 8,
                        showSizeChanger: false
                      }
                    : false
                }
                locale={{
                  emptyText: <div style={{ padding: '40px 0' }}>{translate('promoter.no_records')}</div>
                }}
                columns={[
                  {
                    title: translate('promoter.phone'),
                    dataIndex: 'phone'
                  },
                  {
                    title: translate('promoter.order_amount'),
                    dataIndex: 'amount',
                    render: (amount: number) => <div>{(amount || 0) / 100}</div>
                  },
                  {
                    title: translate('promoter.available_commission'),
                    dataIndex: 'commission',
                    render: (commission: number) => <div>{(commission || 0) / 100}</div>
                  },
                  {
                    title: translate('promoter.create_time'),
                    dataIndex: 'create_time',
                    render: (time: number) => <div>{dayjs(time * 1000).format('YYYY-MM-DD HH:mm')}</div>
                  },
                  {
                    title: translate('promoter.order_status'),
                    dataIndex: 'order_status',
                    render: (status: number) => (
                      <div>
                        {status === 1 ? (
                          <Badge color="green" text={translate('promoter.success')} />
                        ) : (
                          <Badge
                            status="default"
                            text={<span style={{ color: 'rgba(0, 0, 0, 0.4)' }}>{translate('promoter.refunded')}</span>}
                          />
                        )}
                      </div>
                    )
                  },
                  {
                    title: translate('promoter.withdraw_status'),
                    dataIndex: 'withdraw_status',
                    render: (status: number) => (
                      <div>
                        {status === 1 && <Tag color="default">{translate('promoter.cannot_withdraw')}</Tag>}
                        {status === 2 && <Tag color="processing">{translate('promoter.can_withdraw')}</Tag>}
                        {status === 3 && <Tag color="success">{translate('promoter.withdrawn')}</Tag>}
                      </div>
                    )
                  }
                ]}
                dataSource={orders}
              ></Table>
            </div>

            <div className="distribution-table" ref={logsRef}>
              <div className="table-title">{translate('promoter.promoter_details')}</div>
              <Table
                pagination={
                  invites?.length > 8
                    ? {
                        pageSize: 8,
                        showSizeChanger: false
                      }
                    : false
                }
                locale={{
                  emptyText: <div style={{ padding: '40px 0' }}>{translate('promoter.no_records')}</div>
                }}
                columns={[
                  {
                    title: translate('promoter.phone'),
                    dataIndex: 'phone'
                  },
                  {
                    title: translate('promoter.total_order_amount'),
                    dataIndex: 'accumulated_order_amount',
                    render: (amount: number) => <div>{(amount || 0) / 100}</div>
                  },
                  {
                    title: translate('promoter.binding_date'),
                    dataIndex: 'create_time',
                    render: (time: number) => <div>{dayjs(time * 1000).format('YYYY-MM-DD HH:mm')}</div>
                  }
                ]}
                dataSource={invites}
              ></Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Distribution
